import React, { useState } from 'react';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useRequests, useRequest } from '../../hooks/use-requests';
import { useAllTasks, useCreateTask } from '../../hooks/use-tasks';
import { useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  Group,
  Title,
  Text,
  Badge,
  Stack,
  Tabs,
  Grid,
  Loader,
  Center,
  Alert,
  Modal,
  ScrollArea,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import {
  IconEdit,
  IconTrash,
  IconLayoutKanban,
  IconInfoCircle,
  IconPaperclip,
  IconMessageCircle,
  IconProgress,
  IconChevronLeft,
  IconAlertCircle,
  IconChartBar,
  IconCalendar,
} from '@tabler/icons-react';
import { RequestModal } from './components/RequestModal';
import { AttachmentsSection } from './components/AttachmentsSection';
import { CommentsSection } from './components/CommentsSection';
import { StatusWorkflow } from './components/StatusWorkflow';
import { ArticlesSection } from './components/ArticlesSection';
import { RequestTasksSection } from './components/RequestTasksSection';
import { RequestStatus } from '../../types/api';
import { hasClosureDate } from '../../utils/request-status-utils';
import TaskCreateModal from '../../components/modals/TaskCreateModal';
import TaskDetailModal from '../../components/modals/TaskDetailModal';
import TaskProgressDashboard from '../../components/dashboard/TaskProgressDashboard';
import TaskGanttChart from '../../components/gantt/TaskGanttChart';
import { useTaskDashboard } from '../../hooks/use-task-dashboard';
import { useRequestTasks } from '../../hooks/use-request-tasks';
import { PianoSviluppoPdfModal } from '../../components/pdf/PianoSviluppoPdfModal';
import { IconFileTypePdf } from '@tabler/icons-react';

const REQUEST_PRIORITY_LABELS = {
  LOW: 'Bassa',
  MEDIUM: 'Media',
  HIGH: 'Alta',
  URGENT: 'Urgente',
} as const;

const REQUEST_STATUS_LABELS = {
  [RequestStatus.New]: 'Nuova',
  [RequestStatus.PreCampionatura]: 'Pre-Campionatura',
  [RequestStatus.PreCampionaturaCompleted]: 'Pre-Campionatura Completata',
  [RequestStatus.InProgress]: 'In Lavorazione',
  [RequestStatus.PendingCompletion]: 'In Attesa di Completamento',
  [RequestStatus.Completed]: 'Completata',
  [RequestStatus.Cancelled]: 'Annullata',
  [RequestStatus.Suspended]: 'Sospesa',
} as const;

const STATUS_COLORS = {
  [RequestStatus.New]: 'blue',
  [RequestStatus.PreCampionatura]: 'grape',
  [RequestStatus.PreCampionaturaCompleted]: 'indigo',
  [RequestStatus.InProgress]: 'yellow',
  [RequestStatus.PendingCompletion]: 'orange',
  [RequestStatus.Completed]: 'green',
  [RequestStatus.Cancelled]: 'red',
  [RequestStatus.Suspended]: 'gray',
} as const;

export function RequestDetail() {
  const { requestId } = useParams({ strict: false });
  const navigate = useNavigate();
  const { from } = useSearch({ strict: false }) as { from?: string };
  const backTo = from || '/requests';
  const backLabel = from ? 'Torna alla lista' : 'Torna alle richieste';
  const isNew = requestId === 'new';
  const queryClient = useQueryClient();

  // Usa gli hook direttamente ora che QueryClient è disponibile
  const { deleteMutation } = useRequests();
  const createTaskMutation = useCreateTask();
  const requestQuery = useRequest(isNew ? '' : requestId);
  
  // Hook per le statistiche dashboard
  const { stats, userWorkloads, isLoading: isDashboardLoading } = useTaskDashboard(requestId);
  
  // Hook per i task del Gantt
  const { tasks: requestTasks, isLoading: isTasksLoading } = useRequestTasks(requestId);
  
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [taskDetailModalOpen, setTaskDetailModalOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<string | null>('details');
  const [pianoSviluppoPdfModalOpen, setPianoSviluppoPdfModalOpen] = useState(false);

  const handleEditClick = () => {
    setEditModalOpen(true);
  };

  const handleDeleteClick = () => {
    modals.openConfirmModal({
      title: 'Conferma eliminazione',
      children: (
        <Text size="sm">
          Sei sicuro di voler eliminare questa richiesta? Questa azione non può essere annullata.
        </Text>
      ),
      labels: { confirm: 'Elimina', cancel: 'Annulla' },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        deleteMutation.mutate(requestId, {
          onSuccess: () => {
            notifications.show({
              title: 'Richiesta eliminata',
              message: 'La richiesta è stata eliminata con successo',
              color: 'green',
            });
            navigate({ to: backTo });
          },
          onError: () => {
            notifications.show({
              title: 'Errore',
              message: 'Si è verificato un errore durante l\'eliminazione della richiesta',
              color: 'red',
            });
          },
        });
      },
    });
  };

  const handleCreateTask = () => {
    setTaskModalOpen(true);
  };

  const handleTaskCreated = (taskId: number) => {
    // Ricarica i task per aggiornare la sezione task
    setTaskModalOpen(false);
    // Invalida tutte le query relative ai task per forzare il refresh
    queryClient.invalidateQueries({ queryKey: ['request-tasks', requestId] });
    queryClient.invalidateQueries({ queryKey: ['request-tasks-stats', requestId] });
    // Invalida anche le query del dashboard
    queryClient.invalidateQueries({ queryKey: ['task-dashboard-stats', requestId] });
    queryClient.invalidateQueries({ queryKey: ['user-workload-stats', requestId] });
    // Invalida le query generali dei task per aggiornare tutti gli hook
    queryClient.invalidateQueries({ queryKey: ['tasks'] });
  };

  const handleTaskUpdate = () => {
    // Callback per quando i task vengono aggiornati dal RequestTasksSection
    queryClient.invalidateQueries({ queryKey: ['request-tasks', requestId] });
    queryClient.invalidateQueries({ queryKey: ['request-tasks-stats', requestId] });
    // Invalida anche le query del dashboard e del Gantt
    queryClient.invalidateQueries({ queryKey: ['task-dashboard-stats', requestId] });
    queryClient.invalidateQueries({ queryKey: ['user-workload-stats', requestId] });
    queryClient.invalidateQueries({ queryKey: ['tasks'] });
  };

  // Redirect to create modal if new
  if (isNew) {
    return (
      <Card padding="lg">
        <Group justify="space-between" mb="xl">
          <Group>
            <Button
              variant="subtle"
              leftSection={<IconChevronLeft size={16} />}
              onClick={() => navigate({ to: backTo })}
            >
              {backLabel}
            </Button>
          </Group>
        </Group>

        <RequestModal
          opened={true}
          onClose={() => navigate({ to: backTo })}
        />
      </Card>
    );
  }

  // Loading state
  if (requestQuery.isLoading) {
    return (
      <Center h={400}>
        <Stack align="center" gap="md">
          <Loader size="lg" />
          <Text>Caricamento richiesta...</Text>
        </Stack>
      </Center>
    );
  }

  // Error state
  if (requestQuery.isError) {
    return (
      <Card padding="lg">
        <Alert
          icon={<IconAlertCircle size="1rem" />}
          title="Errore nel caricamento"
          color="red"
        >
          Si è verificato un errore durante il caricamento della richiesta.
        </Alert>
      </Card>
    );
  }

  const request = requestQuery.request;
  if (!request) {
    return (
      <Card padding="lg">
        <Alert
          icon={<IconInfoCircle size="1rem" />}
          title="Richiesta non trovata"
          color="blue"
        >
          La richiesta richiesta non è stata trovata.
        </Alert>
      </Card>
    );
  }

  return (
    <Card padding="lg">
      {/* Header */}
      <Group justify="space-between" mb="xl">
        <Group>
          <Button
            variant="subtle"
            leftSection={<IconChevronLeft size={16} />}
            onClick={() => navigate({ to: backTo })}
          >
            {backLabel}
          </Button>
        </Group>
        <Group wrap="wrap">
          <Button
            variant="light"
            color="violet"
            leftSection={<IconFileTypePdf size={16} />}
            onClick={() => setPianoSviluppoPdfModalOpen(true)}
          >
            Genera PDF
          </Button>
          <Button
            variant="light"
            color="blue"
            leftSection={<IconLayoutKanban size={16} />}
            onClick={handleCreateTask}
          >
            Crea Task
          </Button>
          <Button
            variant="light"
            leftSection={<IconEdit size={16} />}
            onClick={handleEditClick}
          >
            Modifica
          </Button>
          <Button
            variant="light"
            color="red"
            leftSection={<IconTrash size={16} />}
            onClick={handleDeleteClick}
            loading={deleteMutation.isPending}
          >
            Elimina
          </Button>
        </Group>
      </Group>

      {/* Request Info */}
      <Stack gap="md" mb="xl">
        <Group justify="space-between" align="flex-start">
          <Stack gap="xs">
            {request.title && (
              <Title order={2}>{request.title}</Title>
            )}
            <Title order={request.title ? 3 : 2} c={request.title ? 'dimmed' : undefined}>
              {request.clientName} - {request.requestType}
            </Title>
            <Group gap="xs">
              <Badge color={STATUS_COLORS[request.status] || 'gray'} variant="filled" style={{ color: 'white' }}>
                {REQUEST_STATUS_LABELS[request.status] || request.status}
              </Badge>
              <Badge variant="outline">
                {request.requestType}
              </Badge>
              <Badge variant="light">
                {request.clientName || 'N/D'}
              </Badge>
            </Group>
          </Stack>
        </Group>

        <Grid>
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Text size="sm" c="dimmed">N. Progressivo</Text>
            <Text fw={500} size="lg">
              {request.progressiveNumberFormatted || 'N/D'}
            </Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Text size="sm" c="dimmed">Data Creazione</Text>
            <Text fw={500}>
              {new Date(request.createdAt).toLocaleDateString('it-IT')}
            </Text>
          </Grid.Col>
          {hasClosureDate(request.status, request.requestType) && request.completedAt && (
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <Text size="sm" c="dimmed">Data di Chiusura</Text>
              <Text fw={500}>
                {new Date(request.completedAt).toLocaleDateString('it-IT')}
              </Text>
            </Grid.Col>
          )}
          {request.preCampionaturaCompletedAt && (
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <Text size="sm" c="dimmed">Fine Pre-Campionatura</Text>
              <Text fw={500}>
                {new Date(request.preCampionaturaCompletedAt).toLocaleDateString('it-IT')}
              </Text>
            </Grid.Col>
          )}
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Text size="sm" c="dimmed">Data Scadenza</Text>
            <Text fw={500}>
              {request.deliveryDate
                ? new Date(request.deliveryDate).toLocaleDateString('it-IT')
                : 'Non specificata'
              }
            </Text>
          </Grid.Col>
          {request.isFromCrossPortal && request.createdByEmail && (
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <Text size="sm" c="dimmed">Inserita da</Text>
              <Text fw={500}>{request.createdByEmail}</Text>
            </Grid.Col>
          )}
          {request.notes && (
            <Grid.Col span={12}>
              <Text size="sm" c="dimmed">Note</Text>
              <Text fw={500}>{request.notes}</Text>
            </Grid.Col>
          )}
        </Grid>
      </Stack>

      {/* Tabs */}
      <Tabs value={activeTab} onChange={setActiveTab}>
        <ScrollArea type="auto">
          <Tabs.List>
            <Tabs.Tab value="details" leftSection={<IconInfoCircle size="0.8rem" />}>
              Dettagli
            </Tabs.Tab>
            <Tabs.Tab value="tasks" leftSection={<IconLayoutKanban size="0.8rem" />}>
              Attività
            </Tabs.Tab>
            <Tabs.Tab value="dashboard" leftSection={<IconChartBar size="0.8rem" />}>
              Dashboard
            </Tabs.Tab>
            <Tabs.Tab value="gantt" leftSection={<IconCalendar size="0.8rem" />}>
              Timeline
            </Tabs.Tab>
            <Tabs.Tab value="attachments" leftSection={<IconPaperclip size="0.8rem" />}>
              Allegati
            </Tabs.Tab>
            <Tabs.Tab value="comments" leftSection={<IconMessageCircle size="0.8rem" />}>
              Commenti
            </Tabs.Tab>
            <Tabs.Tab value="workflow" leftSection={<IconProgress size="0.8rem" />}>
              Workflow
            </Tabs.Tab>
          </Tabs.List>
        </ScrollArea>

        <Tabs.Panel value="details" pt="md">
          <Stack gap="md">
            {request.articles && request.articles.length > 0 ? (
              <Card withBorder>
                <Title order={4} mb="md">Articoli Richiesti</Title>
                <Stack gap="md">
                  {request.articles.map((article, index) => (
                    <Card key={index} withBorder padding="sm" bg="gray.0">
                      <Stack gap="xs">
                        <Group justify="space-between" align="flex-start">
                          <Stack gap={4}>
                            <Text fw={600} size="md">
                              {article.articleName || `Articolo ${article.articleId}`}
                            </Text>
                            {article.articleCode && (
                              <Text size="xs" c="dimmed">Codice: {article.articleCode}</Text>
                            )}
                          </Stack>
                          <Group gap="xs">
                            <Badge variant="light" color="blue">Qtà: {article.quantity}</Badge>
                            {article.workTypes && (
                              <Badge variant="outline" color="grape">{article.workTypes}</Badge>
                            )}
                          </Group>
                        </Group>
                        {article.notes && (
                          <Card withBorder padding="xs" bg="white">
                            <Text size="xs" c="dimmed" mb={4}>Note:</Text>
                            <Text size="sm" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {article.notes}
                            </Text>
                          </Card>
                        )}
                      </Stack>
                    </Card>
                  ))}
                </Stack>
              </Card>
            ) : (
              <Alert>
                <Text>Nessun articolo associato a questa richiesta.</Text>
              </Alert>
            )}
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="tasks" pt="md">
          <RequestTasksSection requestId={request.id} onTaskUpdate={handleTaskUpdate} />
        </Tabs.Panel>

        <Tabs.Panel value="dashboard" pt="md">
          <TaskProgressDashboard
            requestId={request.id}
            stats={stats}
            userWorkloads={userWorkloads}
            isLoading={isDashboardLoading}
          />
        </Tabs.Panel>

        <Tabs.Panel value="gantt" pt="md">
          <TaskGanttChart
            tasks={requestTasks || []}
            isLoading={isTasksLoading}
            onTaskClick={(task) => {
              setSelectedTaskId(parseInt(task.id));
              setTaskDetailModalOpen(true);
            }}
          />
        </Tabs.Panel>

        <Tabs.Panel value="attachments" pt="md">
          <AttachmentsSection requestId={request.id} />
        </Tabs.Panel>

        <Tabs.Panel value="comments" pt="md">
          <CommentsSection
            requestId={request.id}
            crossPortalRequestId={request.crossPortalRequestId?.toString()}
          />
        </Tabs.Panel>

        <Tabs.Panel value="workflow" pt="md">
          <StatusWorkflow request={request} />
        </Tabs.Panel>
      </Tabs>

      {/* Edit Modal */}
      <RequestModal
        opened={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        request={request}
        mode="edit"
      />

      {/* Task Creation Modal */}
      <TaskCreateModal
        opened={taskModalOpen}
        onClose={() => setTaskModalOpen(false)}
        onTaskCreated={handleTaskCreated}
        requestId={request.id}
      />

      {/* Task Detail Modal */}
      {selectedTaskId && (
        <TaskDetailModal
          opened={taskDetailModalOpen}
          onClose={() => {
            setTaskDetailModalOpen(false);
            setSelectedTaskId(null);
          }}
          taskId={selectedTaskId}
        />
      )}

      {/* Piano Sviluppo PDF Modal */}
      <PianoSviluppoPdfModal
        opened={pianoSviluppoPdfModalOpen}
        onClose={() => setPianoSviluppoPdfModalOpen(false)}
        request={request}
      />
    </Card>
  );
}